/**
 * VIP权限系统前端类型定义
 * 与后端VIPTypes.ts保持一致
 */

/**
 * VIP等级枚举
 */
export enum VIPLevel {
  FREE = 0, // 免费用户
  VIP = 1, // VIP会员
  VVIP = 2, // VVIP会员
  ADMIN = 99, // 管理员
}

/**
 * VIP等级代码
 */
export type VIPLevelCode = 'free' | 'vip' | 'vvip' | 'admin';

/**
 * VIP权限配置
 */
export interface VIPPermissions {
  // 基础聊天功能
  text_chat: boolean; // 文字聊天
  voice_chat: boolean; // 语音聊天
  phone_call: boolean; // 语音通话

  // 高级功能
  knowledge_base: boolean; // 知识库
  long_memory: boolean; // 长期记忆

  // 虚拟设备
  virtual_ring: boolean; // 虚拟戒指
  virtual_gateway: boolean; // 虚拟网关

  // MCP工具等级
  mcp_tools: 'basic' | 'extended' | 'all'; // MCP工具可用范围
}

/**
 * VIP数量限制配置
 * -1 表示无限制
 */
export interface VIPLimits {
  role_slots: number; // 角色卡槽数量
  memory_count: number; // 长期记忆条数
  kb_file_count: number; // 知识库文件数量

  // 显示文本(由后端提供)
  role_slots_display?: string;
  memory_count_display?: string;
  kb_file_count_display?: string;
}

/**
 * 用户使用量统计
 */
export interface UserUsage {
  user_id: string;
  role_count: number; // 当前角色数量
  memory_count: number; // 当前记忆条数
  kb_file_count: number; // 当前知识库文件数量
  updated_at: string;
  created_at: string;
}

/**
 * VIP权限信息响应
 */
export interface PermissionInfoResponse {
  vipLevel: VIPLevel;
  vipLevelCode: VIPLevelCode;
  vipLevelName: string;
  vipExpireAt: string | null;
  isExpired: boolean;
  daysRemaining: number | null;
  permissions: VIPPermissions;
  limits: VIPLimits;
  usage: UserUsage;
}

/**
 * 限制检查结果
 */
export interface LimitCheckResult {
  allowed: boolean; // 是否允许操作
  feature: string; // 功能名称
  current: number; // 当前使用量
  limit: number; // 限制数量(-1表示无限制)
  remaining: number; // 剩余数量
  message: string | null; // 提示消息(达到限制时)
}

/**
 * 权限检查结果
 */
export interface PermissionCheckResult {
  allowed: boolean; // 是否允许
  feature: string; // 功能名称
  requiredLevel: VIPLevel; // 需要的VIP等级
  currentLevel: VIPLevel; // 当前VIP等级
  message?: string; // 提示消息
}

/**
 * VIP赠送规则
 */
export interface VIPGiftRule {
  product_code: string;
  product_name: string;
  price: string;
  gift_vip_level: VIPLevel;
  gift_duration_days: number;
  is_active: number;
}

/**
 * VIP激活输入参数
 */
export interface VIPActivationInput {
  productCode: string; // 产品代码
  orderNo?: string; // 订单号(可选)
}

/**
 * 功能类型枚举(用于权限检查)
 */
export const FeatureType = {
  TEXT_CHAT: 'text_chat',
  VOICE_CHAT: 'voice_chat',
  PHONE_CALL: 'phone_call',
  KNOWLEDGE_BASE: 'knowledge_base',
  LONG_MEMORY: 'long_memory',
  VIRTUAL_RING: 'virtual_ring',
  VIRTUAL_GATEWAY: 'virtual_gateway',
  MCP_TOOLS: 'mcp_tools',
} as const;

export type FeatureType = (typeof FeatureType)[keyof typeof FeatureType];

/**
 * 限制类型枚举(用于数量检查)
 */
export const LimitType = {
  ROLE_SLOTS: 'role_slots',
  MEMORY_COUNT: 'memory_count',
  KB_FILE_COUNT: 'kb_file_count',
} as const;

export type LimitType = (typeof LimitType)[keyof typeof LimitType];

/**
 * VIP激活记录
 */
export interface VIPActivationLog {
  id?: number;
  user_id: string;
  product_code: string;
  order_no?: string;
  old_vip_level: VIPLevel;
  new_vip_level: VIPLevel;
  old_expire_at: string | null;
  new_expire_at: string;
  duration_days: number;
  ip_address?: string;
  created_at: string;
}

/**
 * VIP等级配置信息(用于展示)
 */
export interface VIPLevelInfo {
  level: VIPLevel;
  code: VIPLevelCode;
  name: string;
  description: string;
  features: string[];
  limits: VIPLimits;
  price?: string;
}
